import { useQuery } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  Meta,
  Spinner,
  Title,
} from 'aimo-ui';
import ParkingReceiptContent from 'components/receipt/ParkingReceiptContent';
import { READ_PARKING_SESSION } from 'graphql/queries/readParkingSession';
import { useKeyboardVisible } from 'hooks/useKeyboardVisible';
import useReadChargingSession from 'hooks/useReadChargingSession';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CenterColumn,
  CenterFullScreen,
  CenterRow,
  Notification,
  ScreenContainer,
  ScreenWrapper,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  Maybe,
  ParkingSession,
  Query,
  QueryReadParkingSessionArgs,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { chargingSession } from 'utils/chargingTestData';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

const GetSheetLayout = ({
  sessionData,
  chargingSessionId,
  chargingTransactionId,
  chargingPaymentError,
  chargingSessionPrice,
  chargingIsBilling,
}: {
  sessionData?: ParkingSession;
  chargingSessionId: string | undefined;
  chargingTransactionId: Maybe<string> | undefined;
  chargingPaymentError: boolean;
  chargingSessionPrice: number;
  chargingIsBilling: boolean;
}) => {
  if (isWeb) {
    return <ScreenWrapper tabBarHeight={10} />;
  }
  return (
    <ParkingReceiptContent
      sessionData={sessionData}
      chargingSessionId={chargingSessionId}
      chargingPaymentError={chargingPaymentError}
      chargingTransactionId={chargingTransactionId}
      chargingSessionPrice={chargingSessionPrice}
      chargingIsBilling={chargingIsBilling}
    />
  );
};

const ParkingReceiptScreen = () => {
  const { t } = useTranslation();
  const { params } =
    useRoute<RouteProp<RootStackParamList, 'parkingReceipt'>>();
  const { popTo } = useNavigation<RootNavProps>();

  const sessionId = params?.sessionId || '';
  const chargingSessionId = params?.chargingSessionId;
  let sessionData: ParkingSession | undefined;

  const isKeyboardOpen = useKeyboardVisible();

  const { data, loading, startPolling, stopPolling } = useQuery<
    Query,
    QueryReadParkingSessionArgs
  >(READ_PARKING_SESSION, {
    variables: { sessionId },
    skip: sessionId.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  if (data) {
    sessionData = data.readParkingSession as ParkingSession;
    sessionData?.paymentState === 'NotInitiated' ||
    sessionData?.paymentState === 'Pending'
      ? startPolling(2000)
      : stopPolling();
  }
  const isLoading =
    loading ||
    sessionData?.paymentState === 'NotInitiated' ||
    sessionData?.paymentState === 'Pending';
  const isPaymentSuccess =
    sessionData?.paymentState !== 'Failed' &&
    sessionData?.paymentState !== 'Retrying';

  const { chargingSessionData, chargingTransactionId, chargingPaymentError } =
    useReadChargingSession(chargingSessionId, !!chargingSession);

  const chargingSessionPrice =
    chargingSessionData?.readChargingSession?.priceInCents ?? 0;

  const chargingPaymentType =
    chargingSessionData?.readChargingSession?.paymentState;
  const isBilling =
    chargingPaymentType === 'Billing' || chargingPaymentType === 'Billed';
  const isRetrying = !isPaymentSuccess || chargingPaymentError;

  if (isLoading) {
    return (
      <CenterFullScreen {...testIdentifiers('payment-state-loading')}>
        <Spinner />
        <BaseText center>{t('payment.transactionLoading')}</BaseText>
      </CenterFullScreen>
    );
  }

  return (
    <BottomButtonsLayout
      contentBottom={
        <StyledWrapper mb={2} center maxWidth={300}>
          {!isKeyboardOpen && (
            <Button
              {...testIdentifiers('parking-receipt-confirm-button')}
              size="l"
              fullWidth={true}
              text={t('button.close')}
              onPress={() => {
                popTo('mainTabs', {
                  screen: 'parkScreen',
                });
              }}
            />
          )}
        </StyledWrapper>
      }>
      <ScreenContainer>
        {!isLoading && (
          <ScreenWrapper padding={0} tabBarHeight={0}>
            <StyledWrapper center maxWidth={550}>
              <CenterColumn>
                <StyledImage
                  testID="parking-receipt-image"
                  width={173}
                  height={102}
                  center
                  source={require('assets/images/parking-receipt-car.png')}
                />
                <CenterRow>
                  <Title center {...testIdentifiers('parking-receipt-header')}>
                    {isPaymentSuccess && !chargingPaymentError
                      ? t('parking.receipt.header')
                      : t('parking.receipt.paymentFailed')}
                  </Title>
                </CenterRow>
              </CenterColumn>

              {isRetrying && (
                <StyledWrapper mr={10} ml={10} mt={10} center>
                  <Notification>
                    <Meta>{t('parking.receipt.paymentRetrying')}</Meta>
                  </Notification>
                </StyledWrapper>
              )}
            </StyledWrapper>
            {!isLoading && isWeb && (
              <GetSheetLayout
                sessionData={sessionData}
                chargingSessionId={chargingSessionId}
                chargingTransactionId={chargingTransactionId}
                chargingPaymentError={chargingPaymentError}
                chargingSessionPrice={chargingSessionPrice}
                chargingIsBilling={isBilling}
              />
            )}
            <ParkingReceiptContent
              sessionData={sessionData}
              chargingSessionId={chargingSessionId}
              chargingPaymentError={chargingPaymentError}
              chargingTransactionId={chargingTransactionId}
              chargingSessionPrice={chargingSessionPrice}
              chargingIsBilling={isBilling}
            />
          </ScreenWrapper>
        )}
      </ScreenContainer>
    </BottomButtonsLayout>
  );
};

export default ParkingReceiptScreen;
